// ENT-4097 · the single, shared SSO-management authorization predicate. Both the command palette's
// "Set up single sign-on" action (via `src/lib/commandPaletteRoutes.ts`, which re-exports this) and
// the Settings → Security SSO section (`src/pages/Settings.tsx` → `SsoSettingsContainer`) derive the
// viewer's SSO-management right from THIS one function, so the two surfaces can never disagree about
// who may manage SSO (the drift that ENT-4097 spun off from ENT-4073 / PR #2940).

/**
 * The membership-row shape this predicate needs (a structural subset of `OrganizationMember`).
 * Kept minimal so callers can pass the bootstrap membership object directly.
 */
export interface OrgSsoMembershipInput {
  role?: string | null;
  id?: string | null;
  is_breakglass?: boolean | null;
}

/**
 * ENT-4073 R5 / ENT-4097: whether the viewer may actually MANAGE SSO for the active organisation,
 * mirroring the single authorization rule the `org-sso-admin` Edge Function enforces
 * (`index.ts` §authorize): a REAL, non-break-glass `organization_members.admin` row, OR a platform
 * super admin (`user_roles.super_admin`). It deliberately excludes the two membership shapes that
 * satisfy the broader settings-shell predicate (`canManageOrgSettings`) but that org-sso-admin
 * rejects with `sso_not_org_admin`:
 *   • the synthesized MSP-virtual admin the bootstrap read-model mints (`id === 'msp-virtual'`);
 *   • a time-bounded break-glass admin (`is_breakglass` — the frontend mirror of the backend's
 *     `breakglass_session_id`, a recovery credential, not a permanent admin).
 *
 * This is EXPOSURE control only — entitlement and authorization are enforced independently by the
 * backend (ENT-3459 RLS + ENT-3460 `org-sso-admin`); hidden UI is never the access boundary. Its
 * value here is to stop a virtual-MSP / break-glass admin from mounting the SSO data owner only to
 * hit the backend's fail-closed `sso_not_org_admin` denial and see the misleading "couldn't load,
 * refresh" notice (ENT-4097).
 *
 * Kept as a pure function so the membership-shape matrix is unit-tested without rendering the shell.
 */
export function resolveCanManageOrgSso(
  membership: OrgSsoMembershipInput | null | undefined,
  isSuperAdmin: boolean,
): boolean {
  return (
    isSuperAdmin ||
    (membership?.role === 'admin' &&
      membership.id !== 'msp-virtual' &&
      !membership.is_breakglass)
  );
}
